/**
 * Clip trimming, resolved in the sample domain.
 *
 * The governing invariant: CLAMPING CHANGES PAINT GEOMETRY ONLY. It never
 * changes interaction geometry or the authoritative clip samples. A clip whose
 * body is clipped to the viewport still trims against its real start and end,
 * and the values written back are exact samples.
 *
 * Trimming the start also consumes (or restores) source material, so the source
 * offset moves with it — you cannot pull a clip's start further left than there
 * is audio behind it.
 */

import { toBigInt } from './timebase';
import {
  getTimelineStartSample,
  getLengthSamples,
  getSourceStartSample,
  positionPatch
} from './clipPosition';

/** A clip may not be trimmed shorter than this. */
export const MIN_CLIP_SAMPLES = 64n;

/** Logical (unclamped) clip bounds — what interactions must always use. */
export function clipBounds(clip, tempoMap) {
  const startSample = getTimelineStartSample(clip, tempoMap);
  const lengthSamples = getLengthSamples(clip, tempoMap);
  return {
    startSample,
    lengthSamples,
    endSample: startSample + lengthSamples,
    sourceStartSample: getSourceStartSample(clip, tempoMap)
  };
}

/**
 * Trim the START edge to `targetSample`.
 *
 * Dragging right shortens the clip and advances into the source; dragging left
 * lengthens it, bounded by how much source sits before the current start.
 */
export function resolveTrimStart(clip, targetSample, tempoMap, options = {}) {
  const { minLengthSamples = MIN_CLIP_SAMPLES, canExpandLeft = true } = options;
  const { startSample, endSample, sourceStartSample } = clipBounds(clip, tempoMap);

  // Cannot expand further left than the available source (or past zero).
  const earliest = canExpandLeft
    ? (startSample - sourceStartSample > 0n ? startSample - sourceStartSample : 0n)
    : startSample;
  const latest = endSample - minLengthSamples;

  let next = toBigInt(targetSample);
  if (next < earliest) next = earliest;
  if (next > latest) next = latest;
  if (next < 0n) next = 0n;

  const delta = next - startSample; // >0 shortens, <0 lengthens
  const nextSource = sourceStartSample + delta;

  return positionPatch({
    startSample: next,
    lengthSamples: endSample - next,
    sourceStartSample: nextSource < 0n ? 0n : nextSource
  }, tempoMap);
}

/**
 * Trim the END edge to `targetSample`. The start and source offset are
 * untouched — only the length changes.
 */
export function resolveTrimEnd(clip, targetSample, tempoMap, options = {}) {
  const { minLengthSamples = MIN_CLIP_SAMPLES, maxEndSample = null } = options;
  const { startSample } = clipBounds(clip, tempoMap);

  const earliest = startSample + minLengthSamples;
  let next = toBigInt(targetSample);
  if (maxEndSample !== null && next > toBigInt(maxEndSample)) next = toBigInt(maxEndSample);
  if (next < earliest) next = earliest;

  return positionPatch({ lengthSamples: next - startSample }, tempoMap);
}

/**
 * Which edges of a clip are actually on screen.
 *
 * A clamped body must not offer a trim handle at the viewport boundary — that
 * handle would look like the clip's edge while being nothing of the sort.
 */
export function visibleEdges(clip, viewport, tempoMap) {
  const { startSample, endSample } = clipBounds(clip, tempoMap);
  const viewStart = viewport.startSample;
  const viewEnd = viewport.startSample
    + BigInt(Math.round(viewport.widthPx * viewport.samplesPerPixel));

  return {
    startVisible: startSample >= viewStart && startSample <= viewEnd,
    endVisible: endSample >= viewStart && endSample <= viewEnd
  };
}

/**
 * Can this clip be interacted with at all?
 *
 * Interaction uses LOGICAL bounds, never the clamped paint rect, so a clip that
 * is merely clipped stays fully interactive while one that is genuinely
 * offscreen is not a hit-test candidate.
 */
export function isInteractive(clip, viewport, tempoMap) {
  const { startSample, endSample } = clipBounds(clip, tempoMap);
  const viewStart = viewport.startSample;
  const viewEnd = viewport.startSample
    + BigInt(Math.round(viewport.widthPx * viewport.samplesPerPixel));
  return endSample > viewStart && startSample < viewEnd;
}
